import { z } from 'zod';

const intervals = ['1-week-ago', '1-month-ago', '2-months-ago', '3-months-ago', '6-months-ago', '1-year-ago'] as const;

const creationDateSchema = z.enum(intervals);

const aiConfigFilterSchema = z.object({
  created: creationDateSchema.optional(),
  maintainerId: z.string().optional(),
  maintainerTeamKey: z.string().optional(),
  query: z.string().optional(),
  tags: z.array(z.string()).optional(),
  view: z.string().optional(),
  mode: z.array(z.string()).optional(),
});

export type AiConfigFilter = z.infer<typeof aiConfigFilterSchema>;

function parseCreationDate(value: unknown): AiConfigFilter['created'] | undefined {
  const result = creationDateSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

const defaultAiConfigFilter: AiConfigFilter = {};

export function parseUIAiConfigFilterFromAPI(api: unknown, ctx?: z.RefinementCtx): AiConfigFilter {
  const result = aiConfigFilterSchema.safeParse(api);

  if (result.success) {
    return result.data;
  }

  if (ctx) {
    result.error.issues.forEach((issue) => {
      // Spread rather than forward: zod's `addIssue` parameter ends in `& Record<string, unknown>`,
      // and an interface-typed issue has no index signature to satisfy that. The copy carries the
      // same fields.
      ctx.addIssue({ ...issue });
    });
    return z.NEVER;
  }

  throw result.error;
}

const parseValidParams = (searchParams: URLSearchParams) => {
  const params = new URLSearchParams(searchParams);
  params.forEach((value, key) => {
    try {
      if (!value || value === '""' || decodeURIComponent(value) === '""') {
        params.delete(key);
      }
    } catch {
      // if something goes wrong (e.g. invalid encoding), just remove the param
      params.delete(key);
    }
  });
  return params;
};

export function parseUIAiConfigFilterFromBrowser(searchParams: URLSearchParams): AiConfigFilter {
  const filters: AiConfigFilter = structuredClone(defaultAiConfigFilter);
  const validParams = parseValidParams(searchParams);

  if (validParams.has('query')) {
    filters.query = validParams.get('query') ?? undefined;
  }
  if (validParams.has('maintainerId')) {
    filters.maintainerId = validParams.get('maintainerId') ?? undefined;
  }
  if (validParams.has('maintainerTeamKey')) {
    filters.maintainerTeamKey = validParams.get('maintainerTeamKey') ?? undefined;
  }
  if (validParams.has('tag')) {
    filters.tags = validParams.getAll('tag').filter((tag) => tag !== '');
  }
  if (validParams.has('created')) {
    filters.created = parseCreationDate(validParams.get('created'));
  }
  if (validParams.has('view')) {
    filters.view = validParams.get('view') ?? undefined;
  }
  if (validParams.has('mode')) {
    filters.mode = validParams.getAll('mode').filter((mode) => mode !== '');
  }

  return filters;
}

export function serializeUIAiConfigFilterForBrowser(input: AiConfigFilter): URLSearchParams {
  const searchParams = new URLSearchParams();
  const filter = structuredClone(input);

  if (filter.query) {
    searchParams.set('query', filter.query);
  }
  if (filter.maintainerId !== undefined) {
    searchParams.set('maintainerId', filter.maintainerId);
  }
  if (filter.maintainerTeamKey !== undefined) {
    searchParams.set('maintainerTeamKey', filter.maintainerTeamKey);
  }
  if (filter.tags !== undefined) {
    for (const tag of filter.tags) {
      searchParams.append('tag', tag);
    }
  }
  if (filter.created !== undefined) {
    searchParams.set('created', filter.created);
  }
  if (filter.view !== undefined) {
    searchParams.set('view', filter.view);
  }
  if (filter.mode !== undefined) {
    for (const mode of filter.mode) {
      searchParams.append('mode', mode);
    }
  }

  return searchParams;
}
